import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { AuthService } from '../../core/auth.service';
import { Groups } from '../../models/api/vehicle-position.model';
import { Sensor as SensorPharmacy } from '../../models/sensor';
import { AlarmService } from '../../core/alarm.service';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface PharmacyAlarm {
  pharmacyName: string;
  groups: Groups[];
  index: string;
  name: string;
  pharmacyTemperaturesAndHumidity: Sensor[];
  sensors: SensorPharmacy[];
  indexSensor: number;
  alarmList: any[];
}

interface Sensor {
  id: number;
  powerWarning: string;
  temperature: string;
  humidity: string;
}

@Component({
  selector: 'app-pharmacy-alarm-dialog',
  templateUrl: './pharmacy-alarm-dialog.component.html',
  styleUrls: ['./pharmacy-alarm-dialog.component.scss'],
})
export class PharmacyAlarmDialogComponent implements OnDestroy, OnInit {
  @ViewChild('textArea') textArea: ElementRef;
  @Output() alarmEnded = new EventEmitter<boolean>();

  private pharmacyAlarm: PharmacyAlarm;
  text: string;
  isViewMode = true;
  timeout: any;
  alarm: {
    slots: number[];
    slotName: string;
    alarmId: number;
    date: string;
    description: string;
    temperature: string;
    humidity: string;
  };
  alarmIndex = 0;

  constructor(
    public dialogModalRef: MatDialogRef<PharmacyAlarmDialogComponent>,
    private authService: AuthService,
    private alarmService: AlarmService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: PharmacyAlarm
  ) {
    this.pharmacyAlarm = data;
    this.pharmacyAlarm.alarmList.sort((a, b) => b.alarmId - a.alarmId);
    this.alarm = this.formatAlarm(this.data.alarmList[this.alarmIndex]);
  }

  ngOnInit() {
    this.alarmService.stopAutoRefresh();
    this.fetchData();
  }

  formatAlarm(data: any) {
    const formattedDate = `${data.time.date.year}-${String(
      data.time.date.month
    ).padStart(2, '0')}-${String(data.time.date.day).padStart(2, '0')} ${String(
      data.time.time.hour
    ).padStart(2, '0')}:${String(data.time.time.minute).padStart(
      2,
      '0'
    )}:${String(data.time.time.second).padStart(2, '0')}`;
    let findPharmacyTemperaturesAndHumidity;

    if (this.pharmacyAlarm.pharmacyTemperaturesAndHumidity) {
      findPharmacyTemperaturesAndHumidity =
        this.pharmacyAlarm.pharmacyTemperaturesAndHumidity.find(
          (sensor: Sensor) => sensor.id === this.pharmacyAlarm.indexSensor
        );
    }

    return {
      slots: data.slots,
      slotName: this.getSensorName(data),
      alarmId: data.alarmId,
      date: formattedDate,
      temperature: findPharmacyTemperaturesAndHumidity
        ? findPharmacyTemperaturesAndHumidity.temperature
        : '',
      humidity: findPharmacyTemperaturesAndHumidity
        ? findPharmacyTemperaturesAndHumidity.humidity
        : '',
      description: data.description,
    };
  }

  get login(): string {
    return this.authService.login;
  }

  endAlarm(): void {
    this.isViewMode = true;
    this.alarmService
      .endAlarm(this.alarm.alarmId, this.text)
      .pipe(switchMap(() => this.alarmService.fetchDataOnClick()))
      .subscribe(
        () => {
          this.alarmEnded.emit(this.data.alarmList.length > 1);
          this.dialogModalRef.close();
        },
        (error) => {
          console.error('Error ending alarm or fetching data', error);
        }
      );
  }

  close(): void {
    this.dialogModalRef.close();
    this.alarmService.startAutoRefresh();
  }

  edit() {
    this.isViewMode = false;
    this.timeout = setTimeout(() => {
      this.textArea.nativeElement.focus();
      this.cdr.detectChanges();
    });
  }

  getGroup(groups: Groups[]): string {
    if (groups) {
      return groups.map((group) => group.name).join(', ');
    }
  }

  getSensorName(alarm: any): string {
    if (this.pharmacyAlarm.sensors) {
      let findSensor = this.pharmacyAlarm.sensors.find(
        (sensor: SensorPharmacy) =>
          sensor.slot === this.pharmacyAlarm.indexSensor
      );

      if (findSensor) {
        return findSensor.name;
      }
    }

    if (this.pharmacyAlarm.pharmacyTemperaturesAndHumidity) {
      let findSensor = this.pharmacyAlarm.pharmacyTemperaturesAndHumidity.find(
        (sensor: Sensor) => sensor.id === this.pharmacyAlarm.indexSensor
      );

      if (findSensor) {
        return this.translateService.instant(
          'vehicles.vehiclePosition.temperatureTooltip',
          {
            id: findSensor.id,
            lowPower: findSensor.powerWarning,
          }
        );
      }
    }
  }

  get pharmacyTemperaturesAndHumidity() {
    return this.data.pharmacyTemperaturesAndHumidity.filter((sensor, id) =>
      this.alarm.slots.includes(id + 1)
    );
  }

  fetchData() {
    this.alarmService.fetchDataOnClick();
  }

  ngOnDestroy() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.alarmService.startAutoRefresh();
  }

  next(position) {
    const alarmListLength = this.data.alarmList.length;
    if (alarmListLength > 0) {
      const index = this.alarmIndex + position;
      if (index >= alarmListLength) {
        this.alarmIndex = 0;
      } else if (index < 0) {
        this.alarmIndex = alarmListLength - 1;
      } else {
        this.alarmIndex = index;
      }
      this.alarm = this.formatAlarm(this.data.alarmList[this.alarmIndex]);
    }
  }
}
